import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { BASE_URL } from "../utilities/BASE_URL";
import { postWithToken } from "../utilities/fetchWithToken";
import { sendSocketMessage } from './socketMiddleware';
import { freezeTime, unfreezeTime, queueFreeze } from './freezeApi';
import { isOlderRoomEvent } from './roomProtocolAdapter';

export const submitAction = createAsyncThunk(
    "game/submitAction",
    async ({ roomId, actionType, amount }, { rejectWithValue }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/games/action`, { roomId, actionType, amount });
            if (!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Action failed");
            }
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);



// Voluntarily reveal this player's own hole cards during showdown / last-one-standing. The
// reveal comes back to everyone via a SHOWDOWN_REVEAL WS push, so there's no reducer here.
export const showCards = createAsyncThunk(
    "game/showCards",
    async ({ roomId }, { rejectWithValue }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/games/showcards`, { roomId });
            if (!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Failed to show cards");
            }
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

// Reveal only a chosen subset of this player's hole cards (a partial "show"). Fired automatically
// when the determine-winner window opens with cards still selected. Comes back to everyone via the
// same SHOWDOWN_REVEAL push, so there's no reducer here.
export const showSelectedCards = createAsyncThunk(
    "game/showSelectedCards",
    async ({ roomId, cards }, { rejectWithValue }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/games/showcards`, { roomId, cards });
            if (!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Failed to show cards");
            }
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const openGame = createAsyncThunk(
    "game/openGame",
    async({roomId}, { rejectWithValue, dispatch }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/roomslist/opengame`, {roomId});
            const data = await response.json();
            if(!response.ok) {
                return rejectWithValue(data.error || "Failed to open game");
            }
            
            // Subscribe as spectator
            dispatch(sendSocketMessage(`/app/game/${roomId}/spectate`, { roomId }));

            // Restore the authoritative public + private room state (covers refresh/re-open).
            dispatch(fetchGameSnapshot({ roomId }));

            return data;
        } catch(error) {
            return rejectWithValue(error.message);
        }
    }
)

export const openSecondGame = createAsyncThunk(
    "game/openSecondGame",
    async({roomId}, { rejectWithValue, dispatch }) => {
        try{
        const response = await postWithToken(`${BASE_URL}/roomslist/opengame`, {roomId});
        const data = await response.json();
        if(!response.ok) {
            return rejectWithValue(data.error || "Failed to join second game");
        }

        // Restore the authoritative public + private room state (covers refresh/re-open).
        dispatch(fetchGameSnapshot({ roomId }));

        return data;
        } catch(error) {
            return rejectWithValue(error.message);
        }
    }
)

// Lobby-click auto-seat: ask the backend which table + open seat to put the player in
// (it creates a new overflow table if every table they're not already on is full), open
// that table, and flag pendingAutoSeat so GameRoom pops the buy-in prompt for the seat.
export const quickSeat = createAsyncThunk(
    "game/quickSeat",
    async({ parentRoomId, autoJoinBuyIn }, { rejectWithValue, dispatch, getState }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/roomslist/quickseat`, { roomId: parentRoomId });
            const data = await response.json();
            if(!response.ok) {
                return rejectWithValue(data.error || "Failed to find a seat");
            }

            const targetRoomId = data.id;
            // Subscribe + restore any existing seat and pending Power-Up flow.
            dispatch(sendSocketMessage(`/app/game/${targetRoomId}/spectate`, { roomId: targetRoomId }));
            dispatch(fetchGameSnapshot({ roomId: targetRoomId }));

            // First table opens as primary; a second, distinct table opens split-screen.
            const { primaryGameId } = getState().games;
            const asSecondary = primaryGameId != null && primaryGameId !== targetRoomId;

            // Retry path (the table filled up while the player sat on the buy-in prompt):
            // buy straight in at the freshly resolved table/seat with the amount the player
            // already chose, instead of re-opening the buy-in prompt. The reducer skips
            // pendingAutoSeat when autoJoined so no prompt pops.
            if (autoJoinBuyIn != null) {
                await dispatch(joinGame({
                    roomId: targetRoomId,
                    buyIn: autoJoinBuyIn,
                    seatNumber: data.seatNumber,
                })).unwrap();
                return { ...data, asSecondary, autoJoined: true };
            }

            return { ...data, asSecondary };
        } catch(error) {
            return rejectWithValue(typeof error === 'string' ? error : error.message);
        }
    }
)

// "Stand Up": drop the seat (a cash stack is cashed out to the wallet; the cards and a minimum
// re-buy stay saved at this table for an hour)
// but stay at the table as a spectator so the player can pick a new seat. Differs from leaveTable only
// in that we keep the table open and subscribed (see reducer + socketMiddleware).
export const standUp = createAsyncThunk(
    "game/standUp",
    async ({ roomId }, { rejectWithValue }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/games/leavegame`, { roomId });
            const data = await response.json();
            if (!response.ok) {
                return rejectWithValue(data.error || "Failed to stand up");
            }
            // deferred=true → a showdown/determine-winner hold is resolving; the backend
            // removes the seat only after the pot is awarded, so the seat must keep
            // rendering (possibly as a winner!) until the settle's PLAYER_LEFT arrives.
            // awayed=true → a LIVE tournament table: nothing was removed and no chips moved.
            // The seat, its stack and its blind obligations stay; the player is simply parked
            // AWAY (auto check-folding, still dealt in) until they rejoin or bust. Only the
            // client view is torn down.
            return {
                roomId, chips: data.chips, deferred: data.deferred,
                cashedOut: data.cashedOut,
                awayed: data.awayed === true,
                seatKept: data.seatKept === true,
                tableName: data.tableName,
                seatNumber: data.seatNumber,
                returnMinBuyIn: data.returnMinBuyIn,
                returnExpiresAt: data.returnExpiresAt,
                savedPupCards: data.savedPupCards,
            };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
)

export const leaveTable = createAsyncThunk(
    "game/leaveTable",
    async ({ roomId }, { rejectWithValue }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/games/leavegame`, { roomId });
            const data = await response.json();
            if (!response.ok) {
                return rejectWithValue(data.error || "Failed to leave table");
            }
            // awayed=true → a LIVE tournament table: nothing was removed and no chips moved.
            // The seat, its stack and its blind obligations stay; the player is simply parked
            // AWAY (auto check-folding, still dealt in) until they rejoin or bust. Only the
            // client view is torn down.
            //
            // On a CASH table `chips` is the post-cash-out wallet balance (the stack came back
            // to the account) and `returnMinBuyIn` is the floor for sitting back down at this
            // exact table before returnExpiresAt — a rule, not money being held.
            return {
                roomId, chips: data.chips, deferred: data.deferred,
                cashedOut: data.cashedOut,
                awayed: data.awayed === true,
                seatKept: data.seatKept === true,
                tableName: data.tableName,
                seatNumber: data.seatNumber,
                returnMinBuyIn: data.returnMinBuyIn,
                returnExpiresAt: data.returnExpiresAt,
                savedPupCards: data.savedPupCards,
            };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
)

export const joinGame = createAsyncThunk(
    "game/joinGame",
    async({roomId, buyIn, seatNumber}, { rejectWithValue, getState }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/games/joingame`, {roomId, buyInAmount: buyIn, seatNumber});
            if(!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Failed to join game");
            }
            // The backend replies { seatNumber, gameId } where seatNumber is the seat
            // ACTUALLY taken — if the requested seat was grabbed while we sat on the
            // buy-in prompt, the server seats us at another open seat instead of failing.
            // Adopt the server's seat everywhere.
            const data = await response.json();
            const actualSeat = typeof data?.seatNumber === 'number' ? data.seatNumber : seatNumber;

            // The join itself is authoritative over REST (above); the seat is already
            // broadcast to the room by the engine, so there's no WS join message to send.
            const username = getState().playerInfo.username;
            return {
                roomId,
                seatNumber: actualSeat,
                buyIn: typeof data?.buyIn === 'number' ? data.buyIn : buyIn,
                username,
                restoredReturnState: Boolean(data?.restoredReturnState),
                // Post-debit wallet balance, so the top bar is right the moment we sit down
                // (playerInfoSlice reads this).
                chips: data?.chips,
            };
        } catch(error) {
            return rejectWithValue(error.message);
        }
    }
)

export const addChips = createAsyncThunk(
    "game/addChips",
    async ({ roomId, targetStack }, { rejectWithValue }) => {
        try {
            const response = await postWithToken(
                `${BASE_URL}/games/addchips`,
                { roomId, targetStack }
            );
            if (!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Failed to add chips");
            }
            return await response.json();
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
)

// Reconnect reconciliation. The auto-removal "kick" is pushed as a one-shot WebSocket
// message; if it fires while we're disconnected (an idle/backgrounded tab whose socket
// dropped is exactly what gets kicked) it never reaches us, so on reconnect we still
// think we're seated until a manual refresh. This re-verifies our seat against the
// server for a table we believe we're sitting at: if we've been removed, run the same
// client-side cleanup as a live kick.
export const resyncSeat = createAsyncThunk(
    "game/resyncSeat",
    async ({ roomId }, { rejectWithValue, dispatch, getState }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/games/mycards`, { roomId });
            if (!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Failed to resync seat");
            }
            const data = await response.json();
            const stillSeated = data.seatNumber != null && data.seatNumber >= 0;
            const thoughtSeated = getState().games.mySeats[roomId] != null;
            // Server no longer has us seated but our state says we are → we were removed
            // while away. Mirror the live PLAYER_KICKED handling (drop the table, leave
            // to lobby if it was our last, unsubscribe via the middleware).
            if (!stillSeated && thoughtSeated) {
                dispatch(kickedFromGame(roomId));
            }
            return { roomId, seatNumber: data.seatNumber, holeCards: data.holeCards, handInfo: data.handInfo, pupCards: data.pupCards, pupCardInstances: data.pupCardInstances, stagedPupCards: data.stagedPupCards, stagedCommitted: data.stagedCommitted, queuedPupCards: data.queuedPupCards, exchangeRateTwo: data.exchangeRateTwo, lockedPupCards: data.lockedPupCards, queuedFreeze: data.queuedFreeze, queuedFreezeUsed: data.queuedFreezeUsed, freezeQueueClicked: data.freezeQueueClicked };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
)

// Deterministic, authenticated fetch of the current user's own seat + hole cards.
// Triggered when opening a table (refresh recovery) and on every new deal so a
// seated player always sees their hand, independent of the WebSocket push.
export const fetchMyHand = createAsyncThunk(
    "game/fetchMyHand",
    async ({ roomId }, { rejectWithValue }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/games/mycards`, { roomId });
            if (!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Failed to fetch hand");
            }
            const data = await response.json();
            return { roomId, seatNumber: data.seatNumber, holeCards: data.holeCards, handInfo: data.handInfo, pupCards: data.pupCards, pupCardInstances: data.pupCardInstances, stagedPupCards: data.stagedPupCards, stagedCommitted: data.stagedCommitted, queuedPupCards: data.queuedPupCards, exchangeRateTwo: data.exchangeRateTwo, lockedPupCards: data.lockedPupCards, queuedFreeze: data.queuedFreeze, queuedFreezeUsed: data.queuedFreezeUsed, freezeQueueClicked: data.freezeQueueClicked };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
)

// Authoritative reconnect/open snapshot. Unlike /mycards, this single locked read restores
// the public table, private hand/staged cards, protocol versions, and any live Power-Up stack.
export const fetchGameSnapshot = createAsyncThunk(
    "game/fetchGameSnapshot",
    async ({ roomId }, { rejectWithValue, dispatch, getState }) => {
        try {
            const response = await postWithToken(`${BASE_URL}/games/snapshot`, { roomId });
            if (!response.ok) {
                const text = await response.text();
                return rejectWithValue(text || "Failed to fetch game snapshot");
            }
            const data = await response.json();
            // A reconnect/open request can finish after a newer room event. Decide staleness
            // before interpreting the private seat at all: otherwise an old "not seated"
            // snapshot can kick a player whose newer join/deal is already in Redux.
            const currentGame = getState().games.activeGames[roomId];
            if (!currentGame || isOlderRoomEvent(currentGame, data?.game)) {
                return { ...data, roomId, stale: true };
            }
            const seatNumber = data?.privateState?.seatNumber;
            const thoughtSeated = getState().games.mySeats[roomId] != null;
            if (thoughtSeated && !(seatNumber != null && seatNumber >= 0)) {
                dispatch(kickedFromGame(roomId));
                return { roomId, removed: true };
            }
            return { ...data, roomId };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
)

// ACCOUNT-level "has this player EVER clicked a queued-freeze dot" latch (drives the
// first-time hint flash on the stage/phase indicator). Tri-state, biased against flashing:
// null = unknown (never flash until the server explicitly says false), and once true it can
// NEVER be downgraded — a stale payload must not revive a hint the player already dismissed.
function noteFreezeQueueClicked(state, value) {
    if (value === true) state.freezeQueueClicked = true;
    else if (value === false && state.freezeQueueClicked !== true) state.freezeQueueClicked = false;
    // undefined/null (older payload shapes): leave the latch untouched.
}

// Tear down all game-slice state for one table. Power-Up runtime state lives in pupSlice and is
// cleared by socketMiddleware's matching roomCleared action.
function clearRoomState(state, roomId) {
    delete state.mySeats[roomId];
    delete state.myCards[roomId];
    delete state.queuedFreeze[roomId];
    delete state.freezeNotices[roomId];
    delete state.handInfo[roomId];
    delete state.preAction[roomId];
    delete state.chatMessages[roomId];
    delete state.joiningRooms[roomId];
    delete state.lastJoinedAt[roomId];
    delete state.activeGames[roomId];
    delete state.replays[roomId];
}

const gameSlice = createSlice({
    name: 'game',
    initialState: {
        activeGameScreen: false,
        splitScreenMode: false,
        primaryGameId: null,
        primaryGameName: null,
        secondaryGameId: null,
        secondaryGameName: null,
        replays: {},  // { [roomId]: { roomId } } — open previous-hand replay windows, one per table
        activeGames: {},
        mySeats: {},   // { [roomId]: seatNumber }
        myCards: {},        // { [roomId]: ["Ah", "Kd"] }
        queuedFreeze: {},   // { [roomId]: { point: "flop:decision"|"exchange"|null, used: bool } } — this player's PRIVATE queued-freeze selection (hydrated from snapshot/mycards + queueFreeze responses; opponents' selections are never sent)
        freezeQueueClicked: null, // ACCOUNT-level (not per-room): has this player EVER clicked a queued-freeze dot? null = unknown → the first-time hint never flashes until a snapshot/mycards read says false; latched true forever by any click (queueFreeze.pending) or true payload. Deliberately NOT reset by goToGameList/room teardown.
        freezeNotices: {},  // { [roomId]: { text, receivedAt } } — the centered 4s freeze notice ("already executed a queued freeze"); one per room, expired by FreezeCenterNotice
        handInfo: {},       // { [roomId]: "Pair of 7's" } — this player's current best hand, per stage
        preAction: {},      // { [roomId]: 'check-fold' | 'check' | 'check-call-any' } — queued pre-action
        chatMessages: {},   // { [roomId]: [{ username, text, timestamp }] }
        pendingAutoSeat: null, // { roomId, seatNumber } → GameRoom opens the buy-in prompt
        kickedNotice: false, // true → show "kicked due to inactivity" popup
        sngResult: null, // { roomId, gameName, place, payout } → Sit-and-Go-finish popup
        awayNotice: null, // { roomId, tableName, seatNumber } → we left a LIVE tournament table, which only parked our seat AWAY; the lobby offers "Return to table"
        tournamentResult: null, // { tournamentId, roomId, place, payout, cancelled } → multi-table Tournament-finish popup
        buyInCancelled: null, // { roomId, seatNumber, message } → server cancelled a pending buy-in
        joiningRooms: {},   // { [roomId]: true } while a joinGame request is in flight
        lastJoinedAt: {},   // { [roomId]: epochMs } — when the last join fulfilled (stale-response guard)
        loading: false,
        error: null,
    },
    reducers: {
        addActiveGame(state, action) {
            const game = action.payload;
            state.activeGames[game.id] = game;
        },
        updateActiveGame(state, action) {
            const {id, changes} = action.payload;
            if (state.activeGames[id]) {
                const currentVersion = Number(state.activeGames[id].stateVersion);
                const incomingVersion = Number(changes?.stateVersion);
                if (Number.isFinite(currentVersion) && Number.isFinite(incomingVersion)
                    && incomingVersion < currentVersion) {
                    return;
                }
                state.activeGames[id] = { ...state.activeGames[id], ...changes}
            }
        },
        // Pre-action (queued action shown when it isn't your turn). Client-side only: the
        // auto-fire effect submits it the instant it becomes your turn, then clears it.
        setPreAction(state, action) {
            const { roomId, choice } = action.payload;
            state.preAction[roomId] = choice;
        },
        clearPreAction(state, action) {
            const { roomId } = action.payload;
            delete state.preAction[roomId];
        },
        removeActiveGame(state, action) { 
            const gameId = action.payload; // payload is the gameId
            clearRoomState(state, gameId);

            // Reset primary/secondary game IDs if needed
            if (state.primaryGameId === gameId) {
                state.primaryGameId = null;
            }
            if (state.secondaryGameId === gameId) {
                state.secondaryGameId = null;
                state.splitScreenMode = false;
            }
            
            // If no games are left, go back to the game list
            if (Object.keys(state.activeGames).length === 0) {
                state.activeGameScreen = false;
            }
        },
        goToGameList(state) {
            state.activeGameScreen = false;
            state.splitScreenMode = false;
            state.primaryGameId = null;
            state.primaryGameName = null;
            state.secondaryGameId = null;
            state.secondaryGameName = null;
            state.replays = {};
            state.activeGames = {};
            state.mySeats = {};
            state.myCards = {};
            state.queuedFreeze = {};
            state.freezeNotices = {};
            state.handInfo = {};
            state.preAction = {};
            state.chatMessages = {};
            state.joiningRooms = {};
            state.lastJoinedAt = {};
            state.pendingAutoSeat = null;
            state.kickedNotice = false;
            state.sngResult = null;
            state.tournamentResult = null;
            state.buyInCancelled = null;
            state.error = null;
        },
        viewGameList(state) {
            // Go back to rooms list without losing game state
            state.activeGameScreen = false;
        },
        returnToGame(state) {
            state.activeGameScreen = true;
        },
        // Previous-hand replay windows. Each is a screen slot in the layout, keyed by its table's
        // roomId so a table can have at most one replay open (the constraint the control enforces).
        openReplay(state, action) {
            const roomId = action.payload;
            if (roomId == null) return;
            if (!state.replays[roomId]) state.replays[roomId] = { roomId };
        },
        closeReplay(state, action) {
            const roomId = action.payload;
            if (roomId != null) delete state.replays[roomId];
        },
        setMyCards(state, action) {
            const { roomId, cards } = action.payload;
            state.myCards[roomId] = cards;
        },
        setHandInfo(state, action) {
            const { roomId, handInfo } = action.payload;
            state.handInfo[roomId] = handInfo;
        },
        addChatMessage(state, action) {
            const { roomId, message } = action.payload;
            if (!state.chatMessages[roomId]) state.chatMessages[roomId] = [];
            state.chatMessages[roomId].push(message);
        },
        removeGame(state, action) {
            // Remove a game from view without an API call (for viewing-only tables)
            const roomId = action.payload;
            clearRoomState(state, roomId);

            if (state.primaryGameId === roomId) {
                state.primaryGameId = state.secondaryGameId ?? null;
                state.secondaryGameId = null;
                state.splitScreenMode = false;
            } else if (state.secondaryGameId === roomId) {
                state.secondaryGameId = null;
                state.splitScreenMode = false;
            }

            if (!state.primaryGameId) {
                state.activeGameScreen = false;
            }
        },
        kickedFromGame(state, action) {
            // Server auto-removed this player (5-minute sit-out timer expired). The backend
            // already settled the seat (cash return escrow or Sit-and-Go rules), so mirror the client-side
            // cleanup of leaveTable.fulfilled: drop this one game and return to the lobby
            // only when it was the last open table (a split-screen partner table stays open).
            const roomId = action.payload;
            clearRoomState(state, roomId);

            if (state.primaryGameId === roomId) {
                state.primaryGameId = state.secondaryGameId ?? null;
                state.secondaryGameId = null;
                state.splitScreenMode = false;
            } else if (state.secondaryGameId === roomId) {
                state.secondaryGameId = null;
                state.splitScreenMode = false;
            }

            if (!state.primaryGameId) {
                state.activeGameScreen = false;
            }

            // Surface a popup telling the player why they were removed.
            state.kickedNotice = true;
        },
        clearKickedNotice(state) {
            state.kickedNotice = false;
        },
        sngFinished(state, action) {
            // Sit-and-Go finish (bust, leave while running, or victory). The backend has
            // already settled any payout to the wallet and removed the seat (or the whole
            // finished game), so mirror the kicked teardown for this one table, then show
            // the result popup instead of the inactivity notice.
            const { roomId } = action.payload;
            clearRoomState(state, roomId);

            if (state.primaryGameId === roomId) {
                state.primaryGameId = state.secondaryGameId ?? null;
                state.secondaryGameId = null;
                state.splitScreenMode = false;
            } else if (state.secondaryGameId === roomId) {
                state.secondaryGameId = null;
                state.splitScreenMode = false;
            }

            if (!state.primaryGameId) {
                state.activeGameScreen = false;
            }

            state.sngResult = action.payload;
            // Their run is over (they may have busted while parked away) — there is no table
            // left to return to, so drop any stale "you are away, not out" notice.
            state.awayNotice = null;
        },
        clearSngResult(state) {
            state.sngResult = null;
        },
        // The "you're still in that tournament" notice was dismissed, or the player returned.
        clearAwayNotice(state) {
            state.awayNotice = null;
        },
        // The server seated us at a tournament table (launch or late entry). Open it — the seat
        // already exists server-side (no buy-in prompt); the middleware subscribes the room and
        // fetches a snapshot which hydrates the full table state.
        tournamentStarted(state, action) {
            const { roomId, seatNumber, tableName } = action.payload;
            // Also the "Return to table" / /games/myseats re-open path for a parked away seat.
            if (state.awayNotice?.roomId === roomId) state.awayNotice = null;
            if (!state.activeGames[roomId]) {
                state.activeGames[roomId] = { id: roomId, name: tableName };
            }
            state.mySeats[roomId] = seatNumber;
            state.activeGameScreen = true;
            if (state.primaryGameId == null || state.primaryGameId === roomId) {
                state.primaryGameId = roomId;
                state.primaryGameName = tableName;
            } else if (state.secondaryGameId !== roomId) {
                state.secondaryGameId = roomId;
                state.secondaryGameName = tableName;
                state.splitScreenMode = true;
            }
        },
        // The balancer moved us from one table to another between hands. Tear down the old room and
        // open the new one in the SAME split-screen slot (snapshot fetch hydrates it).
        tournamentMoved(state, action) {
            const { fromRoomId, toRoomId, seatNumber, tableName } = action.payload;
            clearRoomState(state, fromRoomId);
            if (state.primaryGameId === fromRoomId) {
                state.primaryGameId = toRoomId;
                state.primaryGameName = tableName || null;
            } else if (state.secondaryGameId === fromRoomId) {
                state.secondaryGameId = toRoomId;
                state.secondaryGameName = tableName || null;
            }
            if (!state.activeGames[toRoomId]) {
                state.activeGames[toRoomId] = { id: toRoomId, name: tableName };
            }
            state.mySeats[toRoomId] = seatNumber;
            state.activeGameScreen = true;
        },
        // Our tournament finish (bust / leave / win) or a cancellation. Tear down our table (if any
        // — a cancellation before launch has no table) and show the result popup.
        tournamentFinished(state, action) {
            const { roomId } = action.payload;
            if (roomId != null) {
                clearRoomState(state, roomId);
                if (state.primaryGameId === roomId) {
                    state.primaryGameId = state.secondaryGameId ?? null;
                    state.secondaryGameId = null;
                    state.splitScreenMode = false;
                } else if (state.secondaryGameId === roomId) {
                    state.secondaryGameId = null;
                    state.splitScreenMode = false;
                }
                if (!state.primaryGameId) {
                    state.activeGameScreen = false;
                }
            }
            state.tournamentResult = action.payload;
            // Same as sngFinished: nothing left to return to.
            state.awayNotice = null;
        },
        clearTournamentResult(state) {
            state.tournamentResult = null;
        },
        // Freeze time: show the centered 4s notice for this table (re-selecting a queue
        // point after this round's queued freeze already fired — the server's exact
        // "already executed a queued freeze" rejection). One notice per room; a newer
        // one replaces the older. FreezeCenterNotice renders it and expires it below.
        freezeNoticeShown(state, action) {
            const { roomId, text } = action.payload;
            if (!text) return;
            state.freezeNotices[roomId] = { text, receivedAt: Date.now() };
        },
        // The notice's 4s timeout fired (or the table closed) — drop it.
        freezeNoticeExpired(state, action) {
            const { roomId } = action.payload;
            delete state.freezeNotices[roomId];
        },
        setBuyInCancelled(state, action) {
            // Server released our pending seat (buy-in not completed in time). GameRoom
            // reacts by closing the buy-in prompt and showing the popup; the seat itself is
            // freed by the accompanying PLAYER_CANCEL_SIT broadcast.
            state.buyInCancelled = action.payload;
        },
        clearBuyInCancelled(state) {
            state.buyInCancelled = null;
        },
        clearPendingAutoSeat(state) {
            state.pendingAutoSeat = null;
        },
    },
    extraReducers: (builder) => {
        builder
          .addCase(openGame.pending, (state) => {
            state.loading = true;
            state.error = null;
          })
          .addCase(openGame.fulfilled, (state, action) => {
            state.loading = false;
            // action.payload should hold the roomInfo from your controller
            // Populate activeGames with the returned game (room) data keyed by its id
            state.activeGames[action.payload.id] = action.payload;
            state.activeGameScreen = true;
            state.primaryGameId = action.payload.id;
            state.primaryGameName = action.payload.name;
          })
          .addCase(openGame.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
          })
          .addCase(openSecondGame.pending, (state) => {
            state.loading = true;
            state.error = null;
          })
          .addCase(openSecondGame.fulfilled, (state, action) => {
            state.loading = false;
            // Add the second game to activeGames
            state.activeGames[action.payload.id] = action.payload;
            state.splitScreenMode = true;
            state.secondaryGameId = action.payload.id;
            state.secondaryGameName = action.payload.name;
            state.activeGameScreen = true;
          })
          .addCase(openSecondGame.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
          })
          .addCase(quickSeat.pending, (state) => {
            state.loading = true;
            state.error = null;
          })
          .addCase(quickSeat.fulfilled, (state, action) => {
            state.loading = false;
            const { asSecondary, seatNumber, autoJoined, ...game } = action.payload;
            const roomId = game.id;
            state.activeGames[roomId] = game;
            state.activeGameScreen = true;
            if (asSecondary) {
              state.splitScreenMode = true;
              state.secondaryGameId = roomId;
              state.secondaryGameName = game.name;
            } else {
              state.primaryGameId = roomId;
              state.primaryGameName = game.name;
            }
            // GameRoom watches this and opens the buy-in prompt for the auto-picked seat.
            // Skipped when the thunk already bought in itself (seat-race retry path).
            if (!autoJoined) {
              state.pendingAutoSeat = { roomId, seatNumber };
            }
          })
          .addCase(quickSeat.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload;
          })
          .addCase(standUp.fulfilled, (state, action) => {
            const {
              roomId, deferred, awayed, returnMinBuyIn, returnExpiresAt, savedPupCards
            } = action.payload;
            // Defensive: Stand Up isn't offered on a live tournament table, but if one reaches
            // the server it only parks the seat AWAY — the seat is still ours, so keep it.
            if (awayed) return;
            // Stay at the table as a spectator: keep activeGames[roomId] + the subscription
            // so empty seats become clickable again and the player can re-sit.
            // Optimistically vacate our own seat in the local players map: when we were the
            // last player the backend deletes the game and sends no PLAYER_LEFT, so nothing
            // else would clear the stale seat. Skip the optimistic vacate when the leave was
            // DEFERRED (a result is resolving): the seat still exists server-side until the
            // pot is awarded, and blanking it would hide the seat mid-showdown; the settle's
            // PLAYER_LEFT broadcast clears it authoritatively moments later.
            const seat = state.mySeats[roomId];
            const game = state.activeGames[roomId];
            if (game && returnMinBuyIn != null) {
              game.returnMinBuyIn = returnMinBuyIn;
              game.returnExpiresAt = returnExpiresAt;
              game.savedPupCards = savedPupCards ?? 0;
            }
            if (!deferred && game?.players && seat != null) {
              delete game.players[seat];
            }
            delete state.mySeats[roomId];
            delete state.myCards[roomId];
            delete state.queuedFreeze[roomId];
            delete state.handInfo[roomId];
            delete state.preAction[roomId];
          })
          .addCase(standUp.rejected, (state, action) => {
            state.error = action.payload;
          })
          .addCase(joinGame.pending, (state, action) => {
            // Tracked so a stale not-seated /games/mycards response (issued before this
            // join) can't wrongly clear the seat the join is about to establish.
            state.joiningRooms[action.meta.arg.roomId] = true;
          })
          .addCase(joinGame.fulfilled, (state, action) => {
            const { roomId, seatNumber, username, buyIn } = action.payload;
            const game = state.activeGames[roomId];
            if (game) {
                if (!game.players) game.players = {};
                game.players[seatNumber] = { username, chips: buyIn };
                delete game.returnMinBuyIn;
                delete game.returnExpiresAt;
                delete game.savedPupCards;
            }
            state.mySeats[roomId] = seatNumber;
            // A seat lifecycle always starts with an empty client-side action queue.
            delete state.preAction[roomId];
            delete state.joiningRooms[roomId];
            state.lastJoinedAt[roomId] = Date.now();
          })
          .addCase(joinGame.rejected, (state, action) => {
            state.error = action.payload;
            if (action.meta?.arg?.roomId != null) delete state.joiningRooms[action.meta.arg.roomId];
          })
          .addCase(addChips.fulfilled, (state, action) => {
            const { roomId, seatNumber, currentStack, pendingTopUp } = action.payload;
            const player = state.activeGames[roomId]?.players?.[seatNumber];
            if (player) {
              player.chips = currentStack;
              player.pendingTopUp = pendingTopUp;
            }
          })
          .addCase(addChips.rejected, (state, action) => {
            state.error = action.payload;
          })
          .addCase(leaveTable.pending, (state, action) => {
            // Close the table client-side the moment Leave is pressed — don't wait for the
            // server round-trip. During a showdown / determine-winner hold the backend
            // deliberately defers the actual seat removal until the pot is awarded, so the
            // response can lag the button press by several seconds; the leaver's view must
            // not. The server remains authoritative for chips: cash escrow includes any pot
            // won during the hold and is restored or settled regardless of this client view.
            const { roomId } = action.meta.arg;
            clearRoomState(state, roomId);

            if (state.primaryGameId === roomId) {
                // Promote secondary to primary if it exists
                state.primaryGameId = state.secondaryGameId ?? null;
                state.secondaryGameId = null;
                state.splitScreenMode = false;
            } else if (state.secondaryGameId === roomId) {
                state.secondaryGameId = null;
                state.splitScreenMode = false;
            }

            // If no games remain, go back to the list
            if (!state.primaryGameId) {
                state.activeGameScreen = false;
            }
          })
          .addCase(leaveTable.fulfilled, (state, action) => {
            // The teardown already happened on .pending. All this adds is the LIVE TOURNAMENT
            // case: nothing was removed and no chips moved — our seat is still there, parked
            // away and still posting blinds — so remember the table and offer a way back. The
            // /games/myseats self-heal on the next socket connect is the other route.
            const { roomId, awayed, tableName, seatNumber } = action.payload || {};
            if (awayed) {
              state.awayNotice = { roomId, tableName, seatNumber };
            }
          })
          .addCase(leaveTable.rejected, (state, action) => {
            // View is already torn down (pending). The seat may still exist server-side; a
            // re-open of the room re-syncs it (openGame → fetchMyHand), and the 5-minute
            // inactivity removal is the backstop if the player never returns.
            state.error = action.payload;
          })
          .addCase(fetchMyHand.fulfilled, (state, action) => {
            const { roomId, seatNumber, holeCards, handInfo, queuedFreeze, queuedFreezeUsed } = action.payload;
            // Account-level, so it hydrates regardless of the per-room seat guards below.
            noteFreezeQueueClicked(state, action.payload.freezeQueueClicked);
            const notSeated = !(seatNumber != null && seatNumber >= 0);
            // Guard against a STALE not-seated response clearing a seat a concurrent join
            // just established (the mycards request may have been issued before the join).
            const joinInFlight = state.joiningRooms[roomId]
                || (state.lastJoinedAt[roomId] != null && Date.now() - state.lastJoinedAt[roomId] < 4000);
            if (!notSeated) {
                state.mySeats[roomId] = seatNumber;
            } else if (state.mySeats[roomId] != null && !joinInFlight) {
                // The server says we are NOT seated here — clear a stale seat instead of
                // keeping it forever. (Classic case: our deferred leave settled while we
                // had the table re-opened; only a PLAYER_LEFT broadcast arrives and that
                // never touched mySeats.)
                delete state.mySeats[roomId];
            }
            if (notSeated && !joinInFlight) delete state.preAction[roomId];
            if (!(notSeated && joinInFlight)) {
                state.myCards[roomId] = holeCards ? holeCards.split(' ') : [];
                state.handInfo[roomId] = handInfo || '';
                state.queuedFreeze[roomId] = { point: queuedFreeze ?? null, used: !!queuedFreezeUsed };
            }
          })
          .addCase(fetchGameSnapshot.fulfilled, (state, action) => {
            const snapshot = action.payload;
            if (!snapshot || snapshot.removed || snapshot.stale) return;
            const { roomId, game, privateState } = snapshot;

            // One acceptance decision gates the entire snapshot. Private cards must never be
            // blanked (nor an old PUP stack resurrected in pupSlice) when the public payload is
            // older than a room event already applied to this table.
            const current = state.activeGames[roomId];
            if (!current || (game && isOlderRoomEvent(current, game))) return;
            if (game) state.activeGames[roomId] = { ...(current || {}), ...game };

            const seatNumber = privateState?.seatNumber;
            if (seatNumber != null && seatNumber >= 0) {
                state.mySeats[roomId] = seatNumber;
            } else {
                delete state.mySeats[roomId];
                delete state.preAction[roomId];
            }
            state.myCards[roomId] = privateState?.holeCards
                ? privateState.holeCards.split(' ') : [];
            state.handInfo[roomId] = privateState?.handInfo || '';
            state.queuedFreeze[roomId] = {
                point: privateState?.queuedFreeze ?? null,
                used: !!privateState?.queuedFreezeUsed,
            };
            noteFreezeQueueClicked(state, privateState?.freezeQueueClicked);
          })
          .addCase(resyncSeat.fulfilled, (state, action) => {
            const { roomId, seatNumber, holeCards, handInfo, queuedFreeze, queuedFreezeUsed } = action.payload;
            noteFreezeQueueClicked(state, action.payload.freezeQueueClicked);
            // Still seated: refresh seat + cards. If removed, kickedFromGame (dispatched
            // inside the thunk) has already torn the table down, so do nothing here.
            if (seatNumber != null && seatNumber >= 0) {
                state.mySeats[roomId] = seatNumber;
                state.myCards[roomId] = holeCards ? holeCards.split(' ') : [];
                state.handInfo[roomId] = handInfo || '';
                state.queuedFreeze[roomId] = { point: queuedFreeze ?? null, used: !!queuedFreezeUsed };
            }
          })
          // Freeze time: the queue response is authoritative for this player's PRIVATE
          // selection (clicking the selected point again toggles it off — queuedFreeze
          // comes back null). Freeze/Unfreeze need no fulfilled reducer: the public state
          // arrives via FREEZE_STARTED/FREEZE_ENDED + the paired GAME_ACTION snapshot.
          // queueFreeze.rejected deliberately skips state.error — GameTable surfaces the
          // "already executed a queued freeze" rejection as the centered 4s notice instead.
          .addCase(queueFreeze.fulfilled, (state, action) => {
            const { roomId, queuedFreeze, queuedFreezeUsed } = action.payload;
            state.queuedFreeze[roomId] = { point: queuedFreeze ?? null, used: !!queuedFreezeUsed };
            noteFreezeQueueClicked(state, action.payload.freezeQueueClicked);
          })
          // The CLICK itself (not the response) retires the first-time hint flash forever —
          // .pending fires synchronously at dispatch, so even a failed/raced request counts.
          // The localStorage belt in freezeHint.js covers refreshes before the server write.
          .addCase(queueFreeze.pending, (state) => {
            state.freezeQueueClicked = true;
          })
          .addCase(freezeTime.rejected, (state, action) => {
            state.error = action.payload;
          })
          .addCase(unfreezeTime.rejected, (state, action) => {
            state.error = action.payload;
          })
          .addCase(submitAction.rejected, (state, action) => {
            state.error = action.payload;
          });
      }
})

export const {
    goToGameList,
    viewGameList,
    returnToGame,
    openReplay,
    closeReplay,
    removeGame,
    setRoomsList,
    addActiveGame,
    updateActiveGame,
    setPreAction,
    clearPreAction,
    removeActiveGame,
    setMyCards,
    setHandInfo,
    addChatMessage,
    kickedFromGame,
    clearKickedNotice,
    freezeNoticeShown,
    freezeNoticeExpired,
    sngFinished,
    clearSngResult,
    clearAwayNotice,
    tournamentStarted,
    tournamentMoved,
    tournamentFinished,
    clearTournamentResult,
    setBuyInCancelled,
    clearBuyInCancelled,
    clearPendingAutoSeat,
} = gameSlice.actions;

export default gameSlice.reducer;
